import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, interval, Observable } from 'rxjs';
import { AccountService } from 'src/app/services/account.service';
import { Environment, EnvironmentService } from '../../services/environment.service';
import { UiService } from '../../services/ui.service';

import { environment } from 'src/environments/environment';
import { takeUntil } from 'rxjs/operators';
import { StorageManagerService } from '../../services/storage-manager.service';
import { AppComponent } from '../../app.component';
import { ChannelsService } from '../../services/channels.service';
import { RemoteConfigService } from '../../services/remote-config.service';
import { DeviceManagementService } from '../../services/device-management.service';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy {

    isSignUp: boolean = false;
    errors: any[];

    currentHost: string;
    destroy$ = new Subject<void>();
    stopTimer$ = new Subject<void>();

    code: string;
    countDown: number;

    _timer = interval(1000);
    get timer(): Observable<number> {
        return this._timer.pipe(takeUntil(this.destroy$), takeUntil(this.stopTimer$));
    }

    readonly deviceHash: string;
    readonly POLLING_INTERVAL = 5;
    readonly KEEP_POLLING_CODE = 202;
    readonly LOGIN_SUCCESS_CODE = 200;
    readonly NO_USER_ACCESS_RECORD_CODE = 204;

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private environment: EnvironmentService,
        private ui: UiService,
        public accountService: AccountService,
        private storageManager: StorageManagerService,
        private app: AppComponent,
        private channelService: ChannelsService,
        public config: RemoteConfigService,
        private deviceManager: DeviceManagementService
    ) {
        this.deviceHash = this.generateGUID();
    }

    async ngOnInit() {

        this.isSignUp = this.route.snapshot.data['isSignUp'];

        if (this.environment.env == Environment.Staging) {
            this.currentHost = 'staging' + environment.domain;
        } else {
            this.currentHost = environment.host;
        }

        this.ui.startLoading();
        await this.refreshCode();
        this.ui.stopLoading();
    }

    async refreshCode() {
        this.stopTimer();
        this.code = (await this.accountService.getSTVCode(this.deviceHash).toPromise()).code;
        this.countDown = 120;
        this.startTimer();
    }

    startTimer() {
        this.timer.subscribe(() => {
            this.countDown--;
            if (this.countDown % this.POLLING_INTERVAL === 0) {
                this.poll();
            }
            if (this.countDown === 0) {
                this.refreshCode();
            }
        });
    }

    stopTimer() {
        this.stopTimer$.next();
    }

    poll() {
        this.accountService.checkLogin(this.deviceHash, this.code)
            .subscribe(async response => {
                if (response.status === this.KEEP_POLLING_CODE) {
                    return;
                }

                if (response.status === this.LOGIN_SUCCESS_CODE) {
                    this.stopTimer();
                    this.ui.startLoading();
                    this.storageManager.setToken(response.body.token);
                    this.storageManager.setExpiry(response.body.expiry);
                    if (this.config.remoteConfig.login_version == 2) {
                        this.storageManager.setAuthTokenV2(response.body);
                    }
                    this.accountService.setAuthenticated(true);
                    try {
                        await Promise.all([
                            this.app.getUserInfo(),
                            this.channelService.refreshUserChannels(),
                            this.deviceManager.addDevice().toPromise()
                        ]);
                    } catch {
                        this.ui.stopLoading();
                        return;
                    }

                    this.ui.stopLoading();
                    this.router.navigate(['/home']);
                    return;
                }

                if (response.status === this.NO_USER_ACCESS_RECORD_CODE) {
                    this.refreshCode();
                    return;
                }
            });
    }

    ngOnDestroy() {
        this.destroy$.next();
    }

    generateGUID(): string {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }

    @HostListener('window:keydown', ['$event'])
    onKeyDown(event: any) {
	    if (event.key === 'Unidentified' && event.keyCode == 27) {
		    this.router.navigateByUrl('/');
    	}
    }
}
